import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Container from "Components/Container/Container";
import { StatusHandler } from "Components/ApiNotification";
import usePost from "Hooks/usePost";
import Spinner from "Components/RequestHandler/Spinner";
import PasswordInput from "form/Inputs/PasswordInput";
import useInput from "form/Hooks/user-input";

const VerifyForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [step, setStep] = useState(1); // 1: enter code, 2: enter new password
  const [codeVerified, setCodeVerified] = useState(false);
  const inputRefs = useRef([]);

  const { postData, isLoading, isError, data } = usePost();
  const {
    postData: postResetPassword,
    isLoading: isResetting,
    isError: resetError,
    data: resetData,
  } = usePost();

  // Password validation
  const validatePassword = (value) => value.length >= 6;

  const {
    value: newPassword,
    isValid: newPasswordIsValid,
    HasError: newPasswordHasError,
    inputChangeHandler: newPasswordChangeHandler,
    inputBlurHandler: newPasswordBlurHandler,
  } = useInput(validatePassword);

  const {
    value: confirmPassword,
    isValid: confirmPasswordIsValid,
    HasError: confirmPasswordHasError,
    inputChangeHandler: confirmPasswordChangeHandler,
    inputBlurHandler: confirmPasswordBlurHandler,
  } = useInput((value) => value === newPassword && value.length >= 6);

  useEffect(() => {
    // Get email from navigation state
    const emailFromState = location.state?.email;
    if (emailFromState) {
      setEmail(emailFromState);
    }
  }, [location]);

  const handleInputChange = (index, value) => {
    if (/^[0-9]*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim().slice(0, 6);

    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      for (let i = 0; i < pastedData.length; i++) {
        if (i < 6) {
          newOtp[i] = pastedData[i];
        }
      }
      setOtp(newOtp);

      if (pastedData.length < 6) {
        inputRefs.current[pastedData.length].focus();
      }
    }
  };

  const handleVerifyCode = async () => {
    const otpCode = otp.join("");
    if (otpCode.length !== 6 || !email) return;

    const res = await postData("auth/verify-reset-code", {
      email,
      code: otpCode,
    });

    if (res?.success) {
      setCodeVerified(true);
      setStep(2);
    }
  };

  const handleResetPassword = async () => {
    if (!newPasswordIsValid || newPassword !== confirmPassword) return;

    const otpCode = otp.join("");
    const res = await postResetPassword("auth/reset-password", {
      email,
      code: otpCode,
      newPassword,
      confirmPassword,
    });

    if (res?.success) {
      // Redirect to login after successful password reset
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    }
  };

  const handleResendCode = async () => {
    if (!email) return;

    await postData("auth/request-password-reset", {
      email,
    });
  };

  // If no email, show email input
  if (!email) {
    return (
      <main>
        <Container>
          <div className="my-10 flex flex-col items-center justify-center">
            <h1 className="text-4xl font-medium text-center font-title">
              {t("auth.resetPassword")}
            </h1>
          </div>

          <div className="w-full max-w-md mx-auto">
            <div className="bg-white p-6">
              <p className="text-gray-600 mb-4 text-center">
                {t("resetPassword.noEmail", "Please start from the forgot password page")}
              </p>
              <Link
                to="/forgot-password"
                className="bg-primary w-full block text-center text-white px-4 py-3 rounded-md text-lg font-medium"
              >
                {t("auth.forgotPassword")}
              </Link>
            </div>
          </div>
        </Container>
      </main>
    );
  }

  return (
    <main>
      <Container>
        <div className="my-10 flex flex-col items-center justify-center">
          <h1 className="text-4xl font-medium text-center font-title">
            {step === 1 ? t("auth.verifyForgotPassword") : t("auth.resetPassword")}
          </h1>
          <StatusHandler error={isError || resetError} data={data || resetData} />
        </div>

        <div className="w-full max-w-md mx-auto">
          <div className="bg-white p-6">
            {step === 1 ? (
              // Step 1: Enter OTP Code
              <>
                <div className="text-center mb-6">
                  <p className="text-gray-600 mb-2">
                    {t("resetPassword.codeSentTo", "We've sent a verification code to:")}
                  </p>
                  <p className="text-lg font-semibold text-primary break-all">
                    {email}
                  </p>
                  <p className="text-gray-500 text-sm mt-2">
                    {t("resetPassword.enterCode", "Please enter the 6-digit verification code")}
                  </p>
                </div>

                <div className="flex justify-center gap-2 mb-6" dir="ltr">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      className="w-12 h-12 border border-primary rounded-md text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  ))}
                </div>

                <button
                  onClick={handleVerifyCode}
                  disabled={otp.join("").length !== 6 || isLoading}
                  className="bg-primary w-full text-white px-4 py-3 rounded-md text-lg font-medium mb-4 hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isLoading ? <Spinner /> : t("resetPassword.verifyCode", "Verify Code")}
                </button>

                <div className="text-center">
                  <p className="text-gray-600 mb-2">
                    {t("auth.didntReceiveCode")}
                  </p>
                  <button
                    onClick={handleResendCode}
                    disabled={isLoading}
                    className="text-primary font-medium hover:underline"
                  >
                    {t("auth.resendCode")}
                  </button>
                </div>
              </>
            ) : (
              // Step 2: Enter New Password
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-600">
                    {t("resetPassword.codeVerified", "Code verified! Please enter your new password.")}
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <PasswordInput
                      label={t("common.newPassword")}
                      name="newPassword"
                      value={newPassword}
                      onChange={newPasswordChangeHandler}
                      onBlur={newPasswordBlurHandler}
                      hasError={newPasswordHasError}
                      errorMessage={t("resetPassword.passwordMinLength", "Password must be at least 6 characters")}
                    />
                  </div>

                  <div>
                    <PasswordInput
                      label={t("common.confirmPassword")}
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={confirmPasswordChangeHandler}
                      onBlur={confirmPasswordBlurHandler}
                      hasError={confirmPasswordHasError || (confirmPassword && newPassword !== confirmPassword)}
                      errorMessage={t("auth.confirmPasswordError")}
                    />
                  </div>
                </div>

                <button
                  onClick={handleResetPassword}
                  disabled={
                    !newPasswordIsValid ||
                    newPassword !== confirmPassword ||
                    isResetting
                  }
                  className="bg-primary w-full text-white px-4 py-3 rounded-md text-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isResetting ? <Spinner /> : t("auth.resetPassword")}
                </button>

                {resetData?.success && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-green-800">
                      {t("resetPassword.success", "Password reset successfully! Redirecting to login...")}
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="mt-6 text-center">
              <Link to="/login" className="text-primary hover:underline">
                {t("auth.backToLogin")}
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </main>
  );
};

export default VerifyForgotPassword;
